/**
 * Context Category Service
 * Fetches categories, sub-categories, and questions from the API.
 * Also handles doc-to-category mapping CRUD.
 */

import { supabase } from '@foundation/utils/supabaseClient';
import { getActiveWorkspaceId } from '../../../header/activeWorkspace';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export type ContextQuestion = {
  id: string;
  categoryId: string;
  text: string;
  inputType: 'yes-no' | 'yes-no-not-sure' | 'single-select' | 'multi-select' | 'text' | 'textarea';
  options: string[] | null;
  placeholder: string | null;
  orderIndex: number;
  usedFor: string | null;
  isRequired: boolean;
};

export type ContextSubcategory = {
  id: string;
  parentId: string;
  name: string;
  description: string | null;
  orderIndex: number;
  isActive: boolean;
};

export type ContextCategory = {
  id: string;
  parentId: null;
  name: string;
  description: string | null;
  orderIndex: number;
  isActive: boolean;
  subcategories: ContextSubcategory[];
  questions: ContextQuestion[];
};

export type DocCategoryMapping = {
  docId: string;
  categoryId: string;
};

// Map DB snake_case row to typed ContextQuestion
function mapQuestion(row: Record<string, unknown>): ContextQuestion {
  return {
    id: row.id as string,
    categoryId: row.category_id as string,
    text: row.text as string,
    inputType: row.input_type as ContextQuestion['inputType'],
    options: (row.options as string[] | null) ?? null,
    placeholder: (row.placeholder as string | null) ?? null,
    orderIndex: (row.order_index as number) ?? 0,
    usedFor: (row.used_for as string | null) ?? null,
    isRequired: (row.is_required as boolean) ?? true,
  };
}

function mapSubcategory(row: Record<string, unknown>): ContextSubcategory {
  return {
    id: row.id as string,
    parentId: row.parent_id as string,
    name: row.name as string,
    description: (row.description as string | null) ?? null,
    orderIndex: (row.order_index as number) ?? 0,
    isActive: (row.is_active as boolean) ?? true,
  };
}

function mapCategory(row: Record<string, unknown>): ContextCategory {
  return {
    id: row.id as string,
    parentId: null,
    name: row.name as string,
    description: (row.description as string | null) ?? null,
    orderIndex: (row.order_index as number) ?? 0,
    isActive: (row.is_active as boolean) ?? true,
    subcategories: ((row.subcategories as Record<string, unknown>[]) ?? []).map(mapSubcategory),
    questions: ((row.questions as Record<string, unknown>[]) ?? []).map(mapQuestion),
  };
}

const getAuthHeaders = async (orgIdOverride?: string) => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const userId = session?.user?.id;
  const orgId = orgIdOverride ?? getActiveWorkspaceId() ?? '';
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'x-user-id': userId || '',
    'x-org-id': orgId,
  };
};

// ── Read ────────────────────────────────────────────────────────────────────

export const fetchContextCategories = async (): Promise<ContextCategory[]> => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE_URL}/api/revisr/context-categories`, { headers });
  if (!res.ok) throw new Error('Failed to fetch context categories');
  const data = await res.json();
  return ((data.categories as Record<string, unknown>[]) ?? []).map(mapCategory);
};

// Same endpoint but includes inactive categories (used by admin UI).
export const fetchContextCategoriesAdmin = async (): Promise<ContextCategory[]> => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE_URL}/api/revisr/context-categories?includeInactive=true`, { headers });
  if (!res.ok) throw new Error('Failed to fetch context categories');
  const data = await res.json();
  return ((data.categories as Record<string, unknown>[]) ?? []).map(mapCategory);
};

// ── Admin write ─────────────────────────────────────────────────────────────

export const createCategory = async (payload: {
  name: string;
  description?: string;
  order_index?: number;
  parent_id?: string;
}): Promise<ContextCategory> => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE_URL}/api/revisr/context-categories`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Failed to create category');
  const data = await res.json();
  return mapCategory(data.category as Record<string, unknown>);
};

export const updateCategory = async (
  id: string,
  updates: { name?: string; description?: string; order_index?: number; is_active?: boolean },
): Promise<ContextCategory> => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE_URL}/api/revisr/context-categories/${id}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error('Failed to update category');
  const data = await res.json();
  return mapCategory(data.category as Record<string, unknown>);
};

export const deleteCategory = async (id: string): Promise<void> => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE_URL}/api/revisr/context-categories/${id}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) throw new Error('Failed to delete category');
};

export const createQuestion = async (
  categoryId: string,
  payload: {
    text: string;
    input_type: string;
    options?: string[];
    placeholder?: string;
    order_index?: number;
    used_for?: string;
    is_required?: boolean;
  },
): Promise<ContextQuestion> => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE_URL}/api/revisr/context-categories/${categoryId}/questions`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Failed to create question');
  const data = await res.json();
  return mapQuestion(data.question as Record<string, unknown>);
};

export const updateQuestion = async (
  categoryId: string,
  questionId: string,
  updates: {
    text?: string;
    input_type?: string;
    options?: string[];
    placeholder?: string;
    order_index?: number;
    used_for?: string;
    is_required?: boolean;
  },
): Promise<ContextQuestion> => {
  const headers = await getAuthHeaders();
  const res = await fetch(
    `${API_BASE_URL}/api/revisr/context-categories/${categoryId}/questions/${questionId}`,
    { method: 'PATCH', headers, body: JSON.stringify(updates) },
  );
  if (!res.ok) throw new Error('Failed to update question');
  const data = await res.json();
  return mapQuestion(data.question as Record<string, unknown>);
};

export const deleteQuestion = async (categoryId: string, questionId: string): Promise<void> => {
  const headers = await getAuthHeaders();
  const res = await fetch(
    `${API_BASE_URL}/api/revisr/context-categories/${categoryId}/questions/${questionId}`,
    { method: 'DELETE', headers },
  );
  if (!res.ok) throw new Error('Failed to delete question');
};

// ── Doc-category mapping ────────────────────────────────────────────────────

export const fetchDocCategoryMapping = async (docId: string): Promise<DocCategoryMapping | null> => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE_URL}/api/revisr/doc-category-map/${docId}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch doc category mapping');
  const data = await res.json();
  if (!data.mapping) return null;
  const m = data.mapping as Record<string, unknown>;
  return { docId: m.doc_id as string, categoryId: m.category_id as string };
};

export const saveDocCategoryMapping = async (docId: string, categoryId: string): Promise<void> => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE_URL}/api/revisr/doc-category-map/${docId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ category_id: categoryId }),
  });
  if (!res.ok) throw new Error('Failed to save doc category mapping');
};

export const removeDocCategoryMapping = async (docId: string): Promise<void> => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE_URL}/api/revisr/doc-category-map/${docId}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) throw new Error('Failed to remove doc category mapping');
};

export const fetchAllDocCategoryMappings = async (): Promise<DocCategoryMapping[]> => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE_URL}/api/revisr/doc-category-map`, { headers });
  if (!res.ok) throw new Error('Failed to fetch doc category mappings');
  const data = await res.json();
  return ((data.mappings as Record<string, unknown>[]) ?? []).map((m) => ({
    docId: m.doc_id as string,
    categoryId: m.category_id as string,
  }));
};

// ── Unlock logic (client-side, no DB call) ─────────────────────────────────

export type StandardAnswers = Record<string, Record<string, string | string[]>>;

export const isCategoryUnlocked = (
  categoryId: string,
  questions: ContextQuestion[],
  answers: StandardAnswers | undefined,
): boolean => {
  const required = questions.filter((q) => q.isRequired);
  if (required.length === 0) return true;
  const categoryAnswers = answers?.[categoryId] ?? {};
  return required.every((q) => {
    const ans = categoryAnswers[q.id];
    if (ans === undefined || ans === null || ans === '') return false;
    if (Array.isArray(ans)) return ans.length > 0;
    return true;
  });
};
